import { type ReactNode, useState } from 'react'
import { MOCK_DECAY_ITEMS } from '../../_mock-data/decay'
import { RiskBar } from '../../shared/charts'
import { TypeDot } from '../../shared/pills'
import { useCoachingStore } from '../../store'
import {
  type DecayCandidate,
  type Format,
  groupDecay,
} from '../_lib/decay-grouping'
import { formatHoursDuration } from '../_lib/duration'
import type { LibraryItem } from '../_lib/filter-engine'
import type { TypeFilter } from '../curriculum-slice'
import { AssignmentActions } from './assignment-actions'

type DecayView = 'topic' | 'subtopic'
type FormatFilter = 'all' | Format
type DecayAssignmentItem = LibraryItem & {
  assignmentMeta: string
  decay: true
}

const TYPE_FILTERS: { id: TypeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'coding', label: 'Coding' },
  { id: 'sd', label: 'System design' },
  { id: 'behavioral', label: 'Behavioral' },
]

const FORMAT_FILTERS: { id: FormatFilter; label: string }[] = [
  { id: 'all', label: 'All formats' },
  { id: 'problem', label: '🧩 Problems' },
  { id: 'flashcards', label: '🃏 Flashcards' },
  { id: 'exercise', label: '⚡ Exercises' },
]

const FORMAT_META: Record<Format, { label: string; color: string }> = {
  problem: { label: '🧩 problem', color: 'var(--cd-text-muted)' },
  flashcards: { label: '🃏 flashcards', color: 'var(--cd-blue)' },
  exercise: { label: '⚡ exercise', color: 'var(--cd-amber)' },
}

const CANDIDATE_CONTEXT = new Map(
  MOCK_DECAY_ITEMS.flatMap((row) =>
    row.candidates.map(
      (candidate) =>
        [
          candidate.id,
          { type: row.type, topic: row.topic, subtopic: row.subtopic },
        ] as const,
    ),
  ),
)

const riskLabel = (risk: number): string => {
  if (risk >= 75) return 'high risk'
  if (risk >= 60) return 'rising'
  return 'watch'
}

const doneLabel = (candidate: DecayCandidate): string => {
  if (candidate.doneCount === 0) return 'new'
  const weeks = candidate.lastDone?.match(/\d+/)?.[0]
  return weeks
    ? `✓ ×${candidate.doneCount} · ${weeks}wk`
    : `✓ ×${candidate.doneCount}`
}

const asLibraryItem = (
  candidate: DecayCandidate,
  groupName: string,
): DecayAssignmentItem => {
  const context = CANDIDATE_CONTEXT.get(candidate.id)
  if (!context) {
    throw new Error(`Missing decay candidate context for ${candidate.id}`)
  }

  const difficulty =
    candidate.difficulty === '—'
      ? // B5 cannot express a not-applicable difficulty. Keep the deck's runtime
        // `—` metadata instead of inventing an Easy/Med/Hard value.
        ('—' as LibraryItem['difficulty'])
      : candidate.difficulty

  return {
    id: candidate.id,
    title: candidate.title,
    type: context.type,
    topic: context.topic,
    subtopic: context.subtopic,
    difficulty,
    stage: 'Decay review',
    est: candidate.est,
    elo: 1520,
    extId: candidate.id,
    plans: [],
    companies: [],
    assignmentMeta: `⚠ decay · ${groupName} · ~${formatHoursDuration(candidate.est)}`,
    decay: true,
  }
}

const chipStyle = (active: boolean): React.CSSProperties => ({
  borderColor: active ? 'var(--cd-text-muted)' : 'var(--cd-border-2)',
  background: active
    ? 'color-mix(in oklab, var(--cd-text) 8%, transparent)'
    : 'transparent',
  color: active ? 'var(--cd-text)' : 'var(--cd-text-muted)',
})

export function DecayTab(): ReactNode {
  const { curriculum } = useCoachingStore()
  const [view, setView] = useState<DecayView>('topic')
  const [format, setFormat] = useState<FormatFilter>('all')
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set())

  if (curriculum.persisted) {
    return (
      <p className="rounded-lg border border-dashed border-(--cd-border-2) px-3 py-4 text-[11px] text-(--cd-text-faint)">
        No recommendations yet — recommendations will appear here once
        generated.
      </p>
    )
  }

  const filteredRows = MOCK_DECAY_ITEMS.filter(
    (row) =>
      curriculum.typeFilter === 'all' || row.type === curriculum.typeFilter,
  )
  const formats: Format[] = format === 'all' ? [] : [format]
  const groups = groupDecay(filteredRows, view, formats)

  const toggleGroup = (key: string): void => {
    setOpenGroups((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div className="space-y-2 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      <div className="flex flex-wrap items-center gap-2 xl:shrink-0">
        <div className="flex gap-0.5 rounded-full border border-(--cd-border) bg-(--cd-bg) p-0.5">
          {(
            [
              ['topic', 'By topic'],
              ['subtopic', 'By subtopic'],
            ] as const
          ).map(([id, label]) => {
            const active = view === id
            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                onClick={() => setView(id)}
                className="rounded-full px-3 py-1 text-[10px] font-bold"
                style={{
                  background: active ? 'var(--cd-tab-active)' : 'transparent',
                  color: active ? 'var(--cd-text)' : 'var(--cd-text-muted)',
                }}
              >
                {label}
              </button>
            )
          })}
        </div>

        <div className="flex flex-wrap gap-1">
          {TYPE_FILTERS.map((filter) => {
            const active = curriculum.typeFilter === filter.id
            return (
              <button
                key={filter.id}
                type="button"
                aria-pressed={active}
                onClick={() => curriculum.setTypeFilter(filter.id)}
                className="rounded-full border px-2.5 py-1 text-[10px] font-semibold"
                style={chipStyle(active)}
              >
                {filter.label}
              </button>
            )
          })}
        </div>

        <span aria-hidden="true" className="h-3.5 w-px bg-(--cd-border)" />

        <div className="flex flex-wrap gap-1">
          {FORMAT_FILTERS.map((filter) => {
            const active = format === filter.id
            return (
              <button
                key={filter.id}
                type="button"
                aria-pressed={active}
                onClick={() => setFormat(filter.id)}
                className="rounded-full border px-2.5 py-1 text-[10px] font-semibold"
                style={chipStyle(active)}
              >
                {filter.label}
              </button>
            )
          })}
        </div>

        <span className="ml-auto text-[10px] text-(--cd-text-faint)">
          sorted by forget risk
        </span>
      </div>

      <div className="flex max-h-[340px] flex-col gap-1.5 overflow-y-auto xl:max-h-none xl:min-h-0 xl:flex-1">
        {groups.map((group) => {
          const open = openGroups.has(group.key)
          return (
            <section
              key={group.key}
              className="rounded-lg border border-(--cd-red)/22 bg-(--cd-red)/[0.045] px-3 py-2"
            >
              <button
                type="button"
                aria-expanded={open}
                aria-label={`${open ? 'Collapse' : 'Expand'} ${group.name} decay group — ${group.type}, last seen ${group.lastSeenWeeks} weeks, ${group.risk}% ${riskLabel(group.risk)}, ${group.candidates.length} options`}
                onClick={() => toggleGroup(group.key)}
                className="grid w-full grid-cols-[12px_10px_minmax(130px,1fr)_auto_84px_auto_auto] items-center gap-2 text-left"
              >
                <span
                  aria-hidden="true"
                  className="text-[10px] text-(--cd-text-muted)"
                >
                  {open ? '▾' : '▸'}
                </span>
                <TypeDot type={group.type} />
                <span className="truncate text-xs font-semibold text-(--cd-text)">
                  {group.name}
                </span>
                <span className="whitespace-nowrap text-[9.5px] text-(--cd-text-faint)">
                  last seen {group.lastSeenWeeks} wks
                </span>
                <RiskBar percent={group.risk} />
                <span className="flex items-center gap-1 whitespace-nowrap text-[9px] font-bold text-(--cd-red)">
                  <span>{group.risk}%</span>
                  <span aria-hidden="true">·</span>
                  <span>{riskLabel(group.risk)}</span>
                </span>
                <span className="whitespace-nowrap text-[9.5px] text-(--cd-text-faint)">
                  {group.candidates.length} options
                </span>
              </button>

              {open ? (
                <div
                  role="table"
                  aria-label={`${group.name} decay candidates`}
                  className="mt-2 space-y-1 border-t border-(--cd-border) pt-2"
                >
                  {group.candidates.map((candidate) => {
                    const formatMeta = FORMAT_META[candidate.format]
                    const completed = candidate.doneCount > 0
                    const item = asLibraryItem(candidate, group.name)
                    return (
                      <div
                        role="row"
                        key={candidate.id}
                        className="flex flex-wrap items-center gap-2 rounded-md border border-(--cd-border) bg-(--cd-bg) px-2.5 py-1.5"
                      >
                        <span
                          role="cell"
                          className="min-w-[160px] flex-1 text-[11px] font-semibold text-(--cd-text)"
                        >
                          {candidate.title}
                        </span>
                        <span
                          role="cell"
                          className="whitespace-nowrap rounded-md border border-(--cd-border) bg-(--cd-inset) px-1.5 py-0.5 text-[8.5px] font-bold"
                          style={{ color: formatMeta.color }}
                        >
                          {formatMeta.label}
                        </span>
                        {candidate.subtopic ? (
                          <span
                            role="cell"
                            className="whitespace-nowrap rounded-full bg-(--cd-inset) px-1.5 py-0.5 text-[9px] text-(--cd-purple)"
                          >
                            {candidate.subtopic}
                          </span>
                        ) : null}
                        <span
                          role="cell"
                          className="whitespace-nowrap text-[9.5px] text-(--cd-text-muted)"
                        >
                          {candidate.format === 'flashcards'
                            ? `${candidate.difficulty} · deck · ~${formatHoursDuration(candidate.est)}`
                            : `${candidate.difficulty} · ~${formatHoursDuration(candidate.est)}`}
                        </span>
                        <span
                          role="cell"
                          className="whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] font-bold"
                          style={{
                            color: completed
                              ? 'var(--cd-green)'
                              : 'var(--cd-text-faint)',
                            background: completed
                              ? 'color-mix(in oklab, var(--cd-green) 10%, transparent)'
                              : 'var(--cd-inset)',
                          }}
                        >
                          {doneLabel(candidate)}
                        </span>
                        <span role="cell" className="flex whitespace-nowrap">
                          <AssignmentActions
                            variant="standard"
                            addLabel={`Add ${candidate.title}`}
                            trayLabel={`Tray ${candidate.title}`}
                            onAdd={() => curriculum.addAuto(item)}
                            onTray={() => curriculum.stageToTray(item)}
                          />
                        </span>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </section>
          )
        })}
      </div>
    </div>
  )
}
